import { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "./AuthContext";
import { send, remove, pending, cachedJSON, newRef } from "./outbox";
import { useAppTheme, useIsMobile, THEMES, NAVY, ACCENT } from "./ThemeContext";

const API = "/api";
const VEHICLE_TYPES = ["Traktor", "Dumperveoauto", "Poolhaagis"];
const WARN = "#dd6b20";
const FONT = '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif';
const RECENT_MS = 15 * 60 * 1000;

const today = () => new Date().toLocaleDateString("sv-SE");
const truckKey = t => t.reg + "_" + t.material;
const loadLocal = key => { try { return JSON.parse(localStorage.getItem(key) || "null"); } catch { return null; } };
const saveLocal = (key, val) => { try { localStorage.setItem(key, JSON.stringify(val)); } catch {} };

// Inside the shell the colours come from ThemeProvider; on its own the page follows the system setting.
function useOperatorTheme() {
  const ctx = useAppTheme();
  const [ownDark, setOwnDark] = useState(() => {
    try { return window.matchMedia("(prefers-color-scheme: dark)").matches; } catch { return false; }
  });
  if (ctx) return { t: ctx.t, dark: ctx.dark, toggle: ctx.toggle, standalone: false };
  return { t: ownDark ? THEMES.dark : THEMES.light, dark: ownDark, toggle: () => setOwnDark(d => !d), standalone: true };
}

export default function OperatorBeta() {
  const navigate = useNavigate();
  const authCtx = useAuth();
  const { t, dark, toggle, standalone } = useOperatorTheme();
  const mobile = useIsMobile();
  const [sites, setSites] = useState([]);
  const [materials, setMaterials] = useState([]);
  const [operatorName, setOperatorName] = useState(() => {
    if (authCtx?.user?.name) return authCtx.user.name;
    try { return localStorage.getItem("op_name") || ""; } catch { return ""; }
  });
  const [nameInput, setNameInput] = useState("");
  const [selectedSite, setSelectedSite] = useState(() => {
    try { return localStorage.getItem("op_site") || ""; } catch { return ""; }
  });
  const [trucks, setTrucks] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [regSuggestions, setRegSuggestions] = useState([]);
  const [newReg, setNewReg] = useState("");
  const [newType, setNewType] = useState("Dumperveoauto");
  const [newMaterial, setNewMaterial] = useState("");
  const [confirmTruck, setConfirmTruck] = useState(null);
  const [editingReg, setEditingReg] = useState(null);
  const [editRegValue, setEditRegValue] = useState("");
  const [sortMode, setSortMode] = useState(() => localStorage.getItem("op_sort") || "alpha");
  const [customOrder, setCustomOrder] = useState(() => { try { return JSON.parse(localStorage.getItem("op_custom_order") || "{}"); } catch { return {}; } });
  const [dragIdx, setDragIdx] = useState(null);
  const [showSortBar, setShowSortBar] = useState(false);
  const dragOverIdx = useRef(null);
  const truckListRef = useRef(null);

  useEffect(() => {
    try { localStorage.setItem("op_name", operatorName); } catch {}
  }, [operatorName]);

  useEffect(() => {
    try { localStorage.setItem("op_site", selectedSite); } catch {}
  }, [selectedSite]);

  useEffect(() => {
    cachedJSON(`${API}/sites`).then(setSites).catch(() => {});
    cachedJSON(`${API}/materials`).then(setMaterials).catch(() => {});
  }, []);

  // Today's trucks and the refs of this phone's taps, kept locally so the screen works offline.
  const dayKey = `${selectedSite}:${operatorName}:${today()}`;
  const trucksKey = `op_trucks:${dayKey}`;
  const tapsKey = `op_taps:${dayKey}`;
  const [taps, setTaps] = useState({});

  useEffect(() => {
    if (!selectedSite || !operatorName) return;
    const local = loadLocal(trucksKey);
    if (local) setTrucks(local);
    setTaps(loadLocal(tapsKey) || {});
    // While taps are still waiting to be sent, the local counts are ahead of the server's.
    if (pending().some(i => i.kind === "delivery")) return;
    fetch(`${API}/deliveries/today?site=${selectedSite}&logged_by=${encodeURIComponent(operatorName)}`, { credentials: "include" })
      .then(r => (r.ok ? r.json() : Promise.reject()))
      .then(data => { setTrucks(data); saveLocal(trucksKey, data); })
      .catch(() => {});
  }, [selectedSite, operatorName, trucksKey, tapsKey]);

  useEffect(() => { if (selectedSite && operatorName) saveLocal(trucksKey, trucks); }, [trucks, trucksKey, selectedSite, operatorName]);
  useEffect(() => { if (selectedSite && operatorName) saveLocal(tapsKey, taps); }, [taps, tapsKey, selectedSite, operatorName]);

  const logLoad = (truck) => {
    const ref = newRef();
    const at = new Date().toISOString();
    setTrucks(prev => {
      const exists = prev.some(t => truckKey(t) === truckKey(truck));
      return exists ? prev.map(t => truckKey(t) === truckKey(truck) ? { ...t, loads: t.loads + 1 } : t)
                    : [...prev, { ...truck, loads: 1 }];
    });
    setTaps(prev => ({ ...prev, [truckKey(truck)]: [...(prev[truckKey(truck)] || []), { ref, at }] }));
    send(`${API}/deliveries`, {
      kind: "delivery", meta: { ref },
      body: { site: selectedSite, reg: truck.reg, vehicle_type: truck.vehicle_type, material: truck.material,
              logged_by: operatorName, client_ref: ref, logged_at: at },
    });
  };

  const handleTap = async (truck, skipCheck = false) => {
    if (!skipCheck) {
      // This phone's own taps answer offline; the server also knows other operators' taps.
      const mine = Object.entries(taps).filter(([k]) => k.startsWith(truck.reg + "_")).flatMap(([, v]) => v);
      const last = mine.length ? Math.max(...mine.map(t => Date.parse(t.at))) : 0;
      if (Date.now() - last < RECENT_MS) {
        setConfirmTruck({ truck, minutesAgo: Math.floor((Date.now() - last) / 60000) });
        return;
      }
      try {
        const res = await fetch(`${API}/deliveries/recent-check?site=${encodeURIComponent(selectedSite)}&reg=${encodeURIComponent(truck.reg)}`, { credentials: "include" });
        const check = res.ok ? await res.json() : {};
        if (check.recent) {
          setConfirmTruck({ truck, minutesAgo: check.minutes_ago });
          return;
        }
      } catch {}
    }
    logLoad(truck);
  };

  const orderKey = selectedSite || "_";
  const getSortedTrucks = () => {
    if (sortMode === "alpha") return [...trucks].sort((a, b) => a.reg.localeCompare(b.reg));
    if (sortMode === "alpha-rev") return [...trucks].sort((a, b) => b.reg.localeCompare(a.reg));
    const order = customOrder[orderKey] || [];
    return [...trucks].sort((a, b) => {
      const ia = order.indexOf(a.reg + "_" + a.material);
      const ib = order.indexOf(b.reg + "_" + b.material);
      if (ia === -1 && ib === -1) return 0;
      if (ia === -1) return 1;
      if (ib === -1) return -1;
      return ia - ib;
    });
  };
  const sortedTrucks = getSortedTrucks();

  const handleSortChange = (mode) => {
    setSortMode(mode);
    localStorage.setItem("op_sort", mode);
    if (mode === "custom" && !customOrder[orderKey]) {
      const currentOrder = getSortedTrucks().map(t => t.reg + "_" + t.material);
      const next = { ...customOrder, [orderKey]: currentOrder };
      setCustomOrder(next);
      localStorage.setItem("op_custom_order", JSON.stringify(next));
    }
  };

  const handleDragStart = (idx) => setDragIdx(idx);
  const handleDragOver = (e, idx) => { e.preventDefault(); dragOverIdx.current = idx; };
  const handleDragEnd = () => {
    if (dragIdx === null || dragOverIdx.current === null || dragIdx === dragOverIdx.current) { setDragIdx(null); return; }
    const items = [...sortedTrucks];
    const [moved] = items.splice(dragIdx, 1);
    items.splice(dragOverIdx.current, 0, moved);
    const newOrder = items.map(t => t.reg + "_" + t.material);
    const next = { ...customOrder, [orderKey]: newOrder };
    setCustomOrder(next);
    localStorage.setItem("op_custom_order", JSON.stringify(next));
    setDragIdx(null);
    dragOverIdx.current = null;
  };

  const touchDragState = useRef({ idx: null, startY: 0, el: null });
  const handleTouchDragStart = (e, idx) => {
    const touch = e.touches[0];
    touchDragState.current = { idx, startY: touch.clientY, el: e.currentTarget };
    setDragIdx(idx);
  };
  const handleTouchDragMove = (e) => {
    if (touchDragState.current.idx === null) return;
    e.preventDefault();
    const touch = e.touches[0];
    const list = truckListRef.current;
    if (!list) return;
    const cards = list.querySelectorAll("[data-truck-idx]");
    for (const card of cards) {
      const rect = card.getBoundingClientRect();
      if (touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
        dragOverIdx.current = parseInt(card.dataset.truckIdx);
        break;
      }
    }
  };
  const handleTouchDragEnd = () => {
    handleDragEnd();
    touchDragState.current = { idx: null, startY: 0, el: null };
  };

  const handleRegTap = (truck) => {
    setEditingReg(truck);
    setEditRegValue(truck.reg);
  };

  const handleRegUpdate = async () => {
    const newReg = editRegValue.trim().toUpperCase();
    if (!newReg || newReg === editingReg.reg) { setEditingReg(null); return; }
    const oldReg = editingReg.reg;
    send(`${API}/deliveries/update-reg`, { method: "PATCH", kind: "update-reg",
      body: { old_reg: oldReg, new_reg: newReg, site: selectedSite } });
    setTrucks(prev => prev.map(t => t.reg === oldReg ? { ...t, reg: newReg } : t));
    setTaps(prev => Object.fromEntries(Object.entries(prev).map(([k, v]) =>
      [k.startsWith(oldReg + "_") ? newReg + k.slice(oldReg.length) : k, v])));
    setEditingReg(null);
  };

  const handleDecrement = async (truck) => {
    if (truck.loads <= 0) return;
    const mine = taps[truckKey(truck)] || [];
    const last = mine[mine.length - 1];
    setTrucks(prev => prev.map(t => truckKey(t) === truckKey(truck) ? { ...t, loads: Math.max(0, t.loads - 1) } : t));
    if (last) {
      setTaps(prev => ({ ...prev, [truckKey(truck)]: mine.slice(0, -1) }));
      // Not sent yet: just take it back out of the outbox.
      if (remove(i => i.meta?.ref === last.ref)) return;
      send(`${API}/deliveries/by-ref/${last.ref}`, { method: "DELETE", kind: "undo" });
    } else {
      send(`${API}/deliveries/last?site=${encodeURIComponent(selectedSite)}&reg=${encodeURIComponent(truck.reg)}`, { method: "DELETE", kind: "undo" });
    }
  };

  const handleAddTruck = async () => {
    if (!newReg.trim() || !newMaterial) return;
    const reg = newReg.trim().toUpperCase();
    const existing = trucks.find(t => t.reg === reg && t.material === newMaterial);
    if (existing) { handleTap(existing); setShowModal(false); setNewReg(""); return; }
    setShowModal(false);
    setNewReg("");
    logLoad({ reg, vehicle_type: newType, material: newMaterial });
  };

  // Same building blocks as the dashboard and the project manager view, with touch targets sized for gloves on site.
  const page = { background: t.bg, color: t.text, fontFamily: FONT, fontVariantNumeric: "tabular-nums" };
  const label = { fontSize: 12, fontWeight: 700, letterSpacing: "0.06em", textTransform: "uppercase", color: t.muted };
  const card = { background: t.panel, border: `1px solid ${t.border}`, borderRadius: mobile ? 12 : 14, boxShadow: "0 1px 2px rgba(0,0,0,0.06)" };
  const pill = `color-mix(in srgb, ${ACCENT} 16%, ${t.panel})`;
  const primaryBtn = { minHeight: 48, padding: "0 18px", background: ACCENT, color: "#fff", border: "none", borderRadius: 8,
    fontSize: 16, fontWeight: 700, cursor: "pointer", fontFamily: "inherit" };
  const secondaryBtn = { ...primaryBtn, background: "transparent", color: t.text, border: `1px solid ${t.border}`, fontWeight: 600 };
  const disabledBtn = { background: t.border, color: t.mutedLight, cursor: "not-allowed" };
  const segBtn = on => ({ minHeight: 44, padding: "0 14px", borderRadius: 8, fontSize: 14, fontWeight: on ? 700 : 500, cursor: "pointer",
    fontFamily: "inherit", background: on ? ACCENT + "18" : "transparent", color: on ? ACCENT : t.muted,
    border: `1px solid ${on ? ACCENT + "44" : t.border}` });
  const field = { width: "100%", boxSizing: "border-box", minHeight: 52, padding: "12px", background: t.bg, color: t.text,
    border: `1px solid ${t.border}`, borderRadius: 8, fontSize: 20, fontWeight: 800, letterSpacing: 2, textAlign: "center", outline: "none",
    fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", colorScheme: dark ? "dark" : "light" };
  const overlay = { position: "fixed", inset: 0, zIndex: 100, background: "rgba(0,0,0,0.45)",
    display: "flex", alignItems: "center", justifyContent: "center", padding: 16 };
  const modal = { background: t.panel, color: t.text, border: `1px solid ${t.border}`, borderRadius: 14, boxShadow: "0 8px 32px rgba(0,0,0,0.18)",
    padding: mobile ? 20 : 24, width: "100%", boxSizing: "border-box", maxHeight: "90vh", overflowY: "auto", fontFamily: FONT };

  const submitName = () => nameInput.trim() && setOperatorName(nameInput.trim().replace(/\b\w/g, c => c.toUpperCase()));
  const logoTile = (size, bg) => (
    <div style={{ width: size, height: size, borderRadius: Math.round(size / 4), background: bg, color: "#fff", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center", fontWeight: 700, fontSize: Math.round(size * 0.45) }}>S</div>
  );
  const backBtn = onClick => (
    <button onClick={onClick} style={{ position: "absolute", top: 12, left: 12, minHeight: 44, padding: "0 12px", background: "none", border: "none",
      color: t.muted, fontSize: 14, fontWeight: 600, cursor: "pointer", fontFamily: "inherit" }}>← Tagasi</button>
  );
  const startPage = { ...page, position: "relative", display: "flex", alignItems: "center", justifyContent: "center", padding: "72px 16px 32px",
    minHeight: standalone ? "100vh" : "calc(100vh - 120px)", boxSizing: "border-box" };
  const startCard = { ...card, width: "100%", maxWidth: 360, boxSizing: "border-box", padding: mobile ? "28px 20px" : "32px 28px",
    display: "flex", flexDirection: "column", alignItems: "center", textAlign: "center" };
  const brand = (
    <>
      {logoTile(44, NAVY)}
      <div style={{ fontWeight: 800, fontSize: 20, letterSpacing: "0.04em", marginTop: 14 }}>STEINMANN OÜ</div>
    </>
  );

  if (!operatorName) return (
    <div style={startPage}>
      {backBtn(() => navigate("/beta"))}
      <div style={startCard}>
        {brand}
        <div style={{ ...label, marginTop: 6, marginBottom: 24 }}>SISESTA OMA NIMI</div>
        <input value={nameInput} onChange={e => setNameInput(e.target.value)}
          onKeyDown={e => e.key === "Enter" && submitName()}
          placeholder="Ees- ja perekonnanimi" autoFocus
          style={{ ...field, fontSize: 18, fontWeight: 600, letterSpacing: 0, fontFamily: "inherit", marginBottom: 12 }} />
        <button onClick={submitName} disabled={!nameInput.trim()}
          style={{ ...primaryBtn, width: "100%", minHeight: 52, fontSize: 17, ...(nameInput.trim() ? {} : disabledBtn) }}>Edasi →</button>
      </div>
    </div>
  );

  if (!selectedSite) return (
    <div style={startPage}>
      {backBtn(() => setOperatorName(""))}
      <div style={startCard}>
        {brand}
        <div style={{ ...label, marginTop: 6 }}>VALI OBJEKT</div>
        <div style={{ color: t.muted, fontSize: 14, marginTop: 4, marginBottom: 24 }}>{operatorName}</div>
        <div style={{ display: "flex", flexDirection: "column", gap: 10, width: "100%" }}>
          {sites.map(s => (
            <button key={s.id} onClick={() => setSelectedSite(s.name)} style={{ ...primaryBtn, width: "100%", minHeight: 60, fontSize: 20, fontWeight: 800 }}>{s.name}</button>
          ))}
        </div>
      </div>
    </div>
  );

  const totalLoads = trucks.reduce((s, t) => s + t.loads, 0);
  const sorting = showSortBar && sortMode === "custom";
  const onNavy = standalone;
  const themeBtn = (
    <button onClick={toggle} aria-label="Vaheta värviteema" style={{
      width: 44, height: 44, borderRadius: 8, fontSize: 16, cursor: "pointer", flexShrink: 0,
      display: "flex", alignItems: "center", justifyContent: "center", fontFamily: "inherit",
      background: onNavy ? "rgba(255,255,255,0.1)" : "transparent", color: onNavy ? "#fff" : t.text,
      border: onNavy ? "none" : `1px solid ${t.border}`,
    }}>{dark ? "☀" : "☾"}</button>
  );

  return (
    <div style={{ ...page, minHeight: standalone ? "100vh" : undefined }}>
      {/* Inside the shell its navy bar is already above the page, so the page header is a light strip; standalone it is the navy bar. */}
      <div style={{
        position: "sticky", top: 0, zIndex: 10, display: "flex", alignItems: "center", gap: 12,
        padding: mobile ? "10px 14px" : "12px 24px",
        background: onNavy ? NAVY : t.panel, color: onNavy ? "#fff" : t.text,
        borderBottom: onNavy ? "none" : `1px solid ${t.border}`, boxShadow: onNavy ? "none" : "0 1px 2px rgba(0,0,0,0.06)",
      }}>
        {onNavy && logoTile(mobile ? 28 : 32, "rgba(255,255,255,0.14)")}
        <div style={{ minWidth: 0, flex: 1, lineHeight: 1.3 }}>
          {onNavy && <div style={{ fontWeight: 700, fontSize: mobile ? 12 : 13, letterSpacing: "0.02em" }}>STEINMANN OÜ</div>}
          <div style={{ fontWeight: 800, fontSize: onNavy ? (mobile ? 14 : 15) : (mobile ? 18 : 20), overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{selectedSite}</div>
          <div style={{ fontSize: mobile ? 12 : 13, color: onNavy ? "rgba(255,255,255,0.72)" : t.muted, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {onNavy ? "LogSite · " : ""}{operatorName}
          </div>
        </div>
        <div style={{ textAlign: "right", flexShrink: 0 }}>
          <div style={{ fontSize: mobile ? 26 : 30, fontWeight: 800, lineHeight: 1.05, color: onNavy ? "#fff" : t.text }}>{totalLoads}</div>
          <div style={{ ...label, fontSize: 10.5, color: onNavy ? "rgba(255,255,255,0.72)" : t.muted }}>KOORMAT TÄNA</div>
        </div>
        {(onNavy || !mobile) && themeBtn}
      </div>

      <div style={{ maxWidth: 720, margin: "0 auto", padding: mobile ? "12px 12px 110px" : "20px 20px 120px", boxSizing: "border-box" }}>
        {trucks.length > 1 && (
          <div style={{ marginBottom: 12 }}>
            <button onClick={() => setShowSortBar(prev => !prev)} aria-expanded={showSortBar} style={{ ...segBtn(showSortBar), display: "inline-flex", alignItems: "center", gap: 8 }}>
              <span style={{ fontSize: 16 }}>⇅</span>
              <span>Sorteeri</span>
            </button>
            {showSortBar && (
              <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                {[["alpha", "A→Z"], ["alpha-rev", "Z→A"], ["custom", "Kohandatud"]].map(([mode, lbl]) => (
                  <button key={mode} onClick={() => handleSortChange(mode)} style={{ ...segBtn(sortMode === mode), flex: 1, padding: "0 6px" }}>{lbl}</button>
                ))}
              </div>
            )}
          </div>
        )}
        <div ref={truckListRef}>
          {trucks.length === 0 && <div style={{ textAlign: "center", color: t.muted, marginTop: 60, fontSize: 15, lineHeight: 1.5 }}>Ühtegi sõidukit ei ole registreeritud.<br />Lisa esimene sõiduk allpool.</div>}
          {sortedTrucks.map((truck, idx) => (
            <div key={truck.reg + "_" + truck.material} data-truck-idx={idx}
              draggable={sorting}
              onDragStart={sorting ? () => handleDragStart(idx) : undefined}
              onDragOver={sorting ? e => handleDragOver(e, idx) : undefined}
              onDragEnd={sorting ? handleDragEnd : undefined}
              onTouchStart={sorting ? e => handleTouchDragStart(e, idx) : undefined}
              onTouchMove={sorting ? handleTouchDragMove : undefined}
              onTouchEnd={sorting ? handleTouchDragEnd : undefined}
              style={{
                ...card, border: `1px solid ${dragIdx === idx ? ACCENT : t.border}`, padding: mobile ? "12px 12px 12px 14px" : "16px 20px", marginBottom: 10,
                display: "flex", alignItems: "center", justifyContent: "space-between", gap: 10,
                opacity: dragIdx === idx ? 0.5 : 1, transition: "opacity 0.15s",
              }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
                {sorting && <div style={{ color: t.mutedLight, fontSize: 18, cursor: "grab", touchAction: "none", width: 32, height: 44, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>☰</div>}
                <div style={{ minWidth: 0 }}>
                  <div
                    onClick={() => handleRegTap(truck)}
                    style={{ fontWeight: 800, fontSize: mobile ? 19 : 20, letterSpacing: "0.06em", color: t.text, cursor: "pointer", padding: "4px 0",
                      textDecoration: "underline", textDecorationStyle: "dotted", textDecorationColor: t.mutedLight, textUnderlineOffset: 4, whiteSpace: "nowrap" }}>{truck.reg}</div>
                  <div style={{ marginTop: 4, display: "flex", alignItems: "center", gap: 6, flexWrap: "wrap" }}>
                    <span style={{ fontSize: 13, fontWeight: 700, color: ACCENT, background: pill, padding: "3px 10px", borderRadius: 999, whiteSpace: "nowrap" }}>{truck.material}</span>
                    <span style={{ color: t.muted, fontSize: 12 }}>{truck.vehicle_type}</span>
                  </div>
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: mobile ? 8 : 10, flexShrink: 0 }}>
                <button onClick={() => handleDecrement(truck)} disabled={truck.loads === 0} aria-label="−1"
                  style={{ width: 48, height: 48, borderRadius: 8, fontSize: 24, fontWeight: 700, lineHeight: 1, fontFamily: "inherit",
                    background: "transparent", border: `1px solid ${t.border}`,
                    color: truck.loads === 0 ? t.mutedLight : t.text, opacity: truck.loads === 0 ? 0.5 : 1,
                    cursor: truck.loads === 0 ? "not-allowed" : "pointer" }}>−</button>
                <div style={{ textAlign: "center", minWidth: 48 }}>
                  <div style={{ fontSize: 32, fontWeight: 800, lineHeight: 1.05, color: t.text }}>{truck.loads}</div>
                  <div style={{ ...label, fontSize: 10 }}>KOORMAT</div>
                </div>
                <button onClick={() => handleTap(truck)} aria-label="+1"
                  style={{ width: 64, height: 64, borderRadius: 10, fontSize: 32, fontWeight: 700, lineHeight: 1, fontFamily: "inherit",
                    background: ACCENT, border: "none", color: "#fff", cursor: "pointer", boxShadow: "0 1px 2px rgba(0,0,0,0.12)" }}>+</button>
              </div>
            </div>
          ))}
        </div>
      </div>

      <div style={{ position: "fixed", bottom: "var(--shell-bottom, 0px)", left: "var(--shell-left, 0px)", right: 0, zIndex: 40,
        padding: mobile ? "10px 12px" : "14px 20px", background: t.panel, borderTop: `1px solid ${t.border}` }}>
        <button onClick={() => {
          setShowModal(true);
          cachedJSON(`${API}/reg-suggestions${selectedSite ? "?site=" + encodeURIComponent(selectedSite) : ""}`)
            .then(setRegSuggestions).catch(() => {});
        }} style={{ ...primaryBtn, display: "block", width: "100%", maxWidth: 680, margin: "0 auto", minHeight: 52, fontSize: 17 }}>+ Lisa sõiduk</button>
      </div>

      {confirmTruck && (
        <div style={overlay}>
          <div style={{ ...modal, maxWidth: 360, textAlign: "center" }}>
            <div style={{ width: 56, height: 56, borderRadius: 999, background: WARN + "1f", margin: "0 auto 14px",
              display: "flex", alignItems: "center", justifyContent: "center", fontSize: 26 }}>⚠️</div>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 8 }}>
              {confirmTruck.truck.reg} juba logitud
            </div>
            <div style={{ color: t.muted, fontSize: 14, marginBottom: 24 }}>
              {confirmTruck.minutesAgo === 0
                ? "Alla minuti tagasi"
                : `${confirmTruck.minutesAgo} min tagasi`} · Kas oled kindel?
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setConfirmTruck(null)} style={{ ...secondaryBtn, flex: 1 }}>
                Tühista
              </button>
              <button onClick={() => { handleTap(confirmTruck.truck, true); setConfirmTruck(null); }} style={{ ...primaryBtn, flex: 2, background: WARN }}>
                Jah, lisa koorem
              </button>
            </div>
          </div>
        </div>
      )}
      {editingReg && (
        <div style={overlay}>
          <div style={{ ...modal, maxWidth: 360, textAlign: "center" }}>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 16 }}>Muuda reg. nr.</div>
            <div style={{ color: t.muted, fontSize: 14, marginBottom: 12 }}>Praegune: <span style={{ color: t.text, fontWeight: 700 }}>{editingReg.reg}</span></div>
            <input
              value={editRegValue}
              onChange={e => setEditRegValue(e.target.value.toUpperCase())}
              onKeyDown={e => e.key === "Enter" && handleRegUpdate()}
              autoFocus
              style={{ ...field, marginBottom: 20 }}
            />
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setEditingReg(null)} style={{ ...secondaryBtn, flex: 1 }}>
                Tühista
              </button>
              <button onClick={handleRegUpdate} style={{ ...primaryBtn, flex: 2 }}>
                Salvesta
              </button>
            </div>
          </div>
        </div>
      )}
      {showModal && (
        <div style={overlay}>
          <div style={{ ...modal, maxWidth: 420 }}>
            <div style={{ fontWeight: 800, fontSize: 18, marginBottom: 18 }}>Lisa sõiduk</div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ ...label, marginBottom: 6 }}>REG. NR.</div>
              <input value={newReg} onChange={e => setNewReg(e.target.value.toUpperCase())} placeholder="nt. 123 ABC"
                list="reg-suggestions"
                style={{ ...field, fontSize: 18, textAlign: "left" }} />
              <datalist id="reg-suggestions">
                {regSuggestions.map(r => <option key={r} value={r} />)}
              </datalist>
            </div>
            <div style={{ marginBottom: 16 }}>
              <div style={{ ...label, marginBottom: 6 }}>SÕIDUKI TÜÜP</div>
              <div style={{ display: "flex", gap: 8 }}>
                {VEHICLE_TYPES.map(vt => (
                  <button key={vt} onClick={() => setNewType(vt)} style={{ ...segBtn(newType === vt), flex: 1, minWidth: 0, padding: "0 4px", fontSize: 13 }}>{vt}</button>
                ))}
              </div>
            </div>
            <div style={{ marginBottom: 22 }}>
              <div style={{ ...label, marginBottom: 6 }}>MATERJAL</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {materials.map(m => (
                  <button key={m.id} onClick={() => setNewMaterial(m.name)} style={segBtn(newMaterial === m.name)}>{m.name}</button>
                ))}
              </div>
            </div>
            <div style={{ display: "flex", gap: 10 }}>
              <button onClick={() => setShowModal(false)} style={{ ...secondaryBtn, flex: 1 }}>Tühista</button>
              <button onClick={handleAddTruck} style={{ ...primaryBtn, flex: 2, opacity: newReg.trim() && newMaterial ? 1 : 0.5 }}>Lisa</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
